import React from 'react'
import { Input } from '@/components/ui/Input'
import { Trash2, Plus, ChevronDown, ChevronUp, AlertTriangle, CheckCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { BonusCategoria, BonusIndicador } from './types'

interface TemplateFormProps {
  name: string
  onNameChange: (name: string) => void
  categorias: BonusCategoria[]
  onCategoriasChange: (categorias: BonusCategoria[]) => void
  pesoTotal: number
  isPesoValid: boolean
}

function generateId(): string {
  return Math.random().toString(36).substring(2, 10)
}

function createEmptyIndicador(catNome?: string): BonusIndicador {
  return {
    id: generateId(),
    nome: catNome ? `Indicador ${catNome}` : 'Indicador',
    peso: 100,
    meta: 1,
    unidade: '%',
    fonte: 'manual',
  }
}

function createEmptyCategoria(): BonusCategoria {
  const nome = 'Nova Categoria'
  return {
    id: generateId(),
    nome,
    peso: 0,
    salarios: 1,
    teto: 100,
    binario: false,
    indicadores: [createEmptyIndicador(nome)],
  }
}

export function TemplateForm({
  name,
  onNameChange,
  categorias,
  onCategoriasChange,
  pesoTotal,
  isPesoValid,
}: TemplateFormProps) {
  const [expandedCat, setExpandedCat] = React.useState<string | null>(null)

  const updateCategoria = (index: number, updates: Partial<BonusCategoria>) => {
    const updated = [...categorias]
    updated[index] = { ...updated[index], ...updates }
    onCategoriasChange(updated)
  }

  const removeCategoria = (index: number) => {
    onCategoriasChange(categorias.filter((_, i) => i !== index))
  }

  const addCategoria = () => {
    const newCat = createEmptyCategoria()
    onCategoriasChange([...categorias, newCat])
    setExpandedCat(newCat.id)
  }

  const updateIndicador = (catIndex: number, indIndex: number, updates: Partial<BonusIndicador>) => {
    const updated = [...categorias]
    const indicadores = [...updated[catIndex].indicadores]
    indicadores[indIndex] = { ...indicadores[indIndex], ...updates }
    updated[catIndex] = { ...updated[catIndex], indicadores }
    onCategoriasChange(updated)
  }

  const removeIndicador = (catIndex: number, indIndex: number) => {
    const updated = [...categorias]
    const indicadores = updated[catIndex].indicadores.filter((_, i) => i !== indIndex)
    if (indicadores.length === 0) return // must have at least 1
    updated[catIndex] = { ...updated[catIndex], indicadores }
    onCategoriasChange(updated)
  }

  const addIndicador = (catIndex: number) => {
    const updated = [...categorias]
    updated[catIndex] = {
      ...updated[catIndex],
      indicadores: [...updated[catIndex].indicadores, createEmptyIndicador()],
    }
    onCategoriasChange(updated)
  }

  return (
    <div className="space-y-4">
      {/* Template Name */}
      <div>
        <label className="block text-sm text-brand-gray mb-1">Nome do Template</label>
        <Input
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="Ex: Bonificacao Diretores 2025"
          className="bg-brand-dark-lighter border-brand-gray/30 text-white"
        />
      </div>

      {/* Categorias */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm text-brand-gray">Categorias</label>
          <div className={cn(
            'text-xs px-2 py-0.5 rounded',
            isPesoValid ? 'bg-green-900/40 text-green-400' : 'bg-red-900/40 text-red-400',
          )}>
            {isPesoValid ? (
              <><CheckCircle className="inline h-3 w-3 mr-1" />Total: {pesoTotal}%</>
            ) : (
              <><AlertTriangle className="inline h-3 w-3 mr-1" />Total: {pesoTotal}% (deve ser 100%)</>
            )}
          </div>
        </div>

        <div className="space-y-2">
          {categorias.map((cat, catIdx) => {
            const isExpanded = expandedCat === cat.id
            return (
              <div
                key={cat.id}
                className="border border-brand-gray/20 rounded-monumenta bg-brand-dark-lighter"
              >
                {/* Categoria Header */}
                <div className="flex items-center gap-2 p-3">
                  <button
                    type="button"
                    className="text-brand-gray hover:text-white transition-colors"
                    onClick={() => setExpandedCat(isExpanded ? null : cat.id)}
                  >
                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                  <Input
                    value={cat.nome}
                    onChange={(e) => updateCategoria(catIdx, { nome: e.target.value })}
                    placeholder="Nome da categoria"
                    className="flex-1 bg-transparent border-none text-white text-sm h-8 p-0 focus:ring-0"
                  />
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-brand-gray">Peso:</span>
                    <Input
                      type="number"
                      value={cat.peso}
                      onChange={(e) => updateCategoria(catIdx, { peso: Number(e.target.value) })}
                      className="w-16 bg-transparent border-brand-gray/30 text-white text-sm h-8 text-center"
                      min={0}
                      max={100}
                    />
                    <span className="text-xs text-brand-gray">%</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-brand-gray">Sal:</span>
                    <Input
                      type="number"
                      value={cat.salarios}
                      onChange={(e) => updateCategoria(catIdx, { salarios: Number(e.target.value) })}
                      className="w-14 bg-transparent border-brand-gray/30 text-white text-sm h-8 text-center"
                      min={0}
                      step={0.5}
                    />
                  </div>
                  <button
                    type="button"
                    className="text-red-400 hover:text-red-300 transition-colors p-1"
                    onClick={() => removeCategoria(catIdx)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                {/* Expanded: Indicadores */}
                {isExpanded && (
                  <div className="border-t border-brand-gray/20 p-3 space-y-2">
                    <label className="text-xs text-brand-gray">Indicadores</label>
                    {cat.indicadores.map((ind, indIdx) => (
                      <div key={ind.id} className="flex items-center gap-2 pl-4">
                        <Input
                          value={ind.nome}
                          onChange={(e) => updateIndicador(catIdx, indIdx, { nome: e.target.value })}
                          placeholder="Nome do indicador"
                          className="flex-1 bg-transparent border-brand-gray/30 text-white text-xs h-7"
                        />
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-brand-gray">Peso:</span>
                          <Input
                            type="number"
                            value={ind.peso}
                            onChange={(e) => updateIndicador(catIdx, indIdx, { peso: Number(e.target.value) })}
                            className="w-14 bg-transparent border-brand-gray/30 text-white text-xs h-7 text-center"
                          />
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-brand-gray">Meta:</span>
                          <Input
                            type="number"
                            value={ind.meta}
                            onChange={(e) => updateIndicador(catIdx, indIdx, { meta: Number(e.target.value) })}
                            className="w-14 bg-transparent border-brand-gray/30 text-white text-xs h-7 text-center"
                          />
                        </div>
                        {cat.indicadores.length > 1 && (
                          <button
                            type="button"
                            className="text-red-400 hover:text-red-300 transition-colors p-0.5"
                            onClick={() => removeIndicador(catIdx, indIdx)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      className="text-xs text-brand-primary hover:text-brand-primary/80 transition-colors pl-4 flex items-center gap-1"
                      onClick={() => addIndicador(catIdx)}
                    >
                      <Plus className="h-3 w-3" /> Adicionar indicador
                    </button>

                    {/* Gate config */}
                    <div className="flex items-center gap-2 pl-4 pt-2 border-t border-brand-gray/10">
                      <span className="text-xs text-brand-gray">Teto:</span>
                      <Input
                        type="number"
                        value={cat.teto}
                        onChange={(e) => updateCategoria(catIdx, { teto: Number(e.target.value) })}
                        className="w-16 bg-transparent border-brand-gray/30 text-white text-xs h-7 text-center"
                      />
                      <span className="text-xs text-brand-gray">%</span>
                      <span className="text-xs text-brand-gray ml-4">Gate:</span>
                      <select
                        value={cat.gateCat || ''}
                        onChange={(e) => updateCategoria(catIdx, {
                          gateCat: e.target.value || undefined,
                          gateMin: e.target.value ? (cat.gateMin || 70) : undefined,
                        })}
                        className="bg-transparent border border-brand-gray/30 rounded text-white text-xs h-7 px-1"
                      >
                        <option value="">Nenhum</option>
                        {categorias.filter(c => c.id !== cat.id).map(c => (
                          <option key={c.id} value={c.id}>{c.nome || `Cat ${c.id.slice(0, 4)}`}</option>
                        ))}
                      </select>
                      {cat.gateCat && (
                        <>
                          <span className="text-xs text-brand-gray">Min:</span>
                          <Input
                            type="number"
                            value={cat.gateMin || 70}
                            onChange={(e) => updateCategoria(catIdx, { gateMin: Number(e.target.value) })}
                            className="w-14 bg-transparent border-brand-gray/30 text-white text-xs h-7 text-center"
                          />
                          <span className="text-xs text-brand-gray">%</span>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        <button
          type="button"
          className="mt-2 text-sm text-brand-primary hover:text-brand-primary/80 transition-colors flex items-center gap-1"
          onClick={addCategoria}
        >
          <Plus className="h-4 w-4" /> Adicionar Categoria
        </button>
      </div>
    </div>
  )
}
